import type { ReactNode } from 'react';
import type { InboxTier } from '@/domain/types';
import { AlertIcon, CheckCircleIcon, QuestionIcon } from '../icons';
import { TIER_LABEL } from '../strings';
import './Badge.css';

export type BadgeTone = 'neutral' | 'brand' | 'gold' | 'danger' | 'info';

export interface BadgeProps {
  children: ReactNode;
  tone?: BadgeTone;
  /** Small dot before the text. */
  dot?: boolean;
  className?: string;
  title?: string;
}

/** Small label chip: counts, states, "Saknar kvitto". */
export function Badge({ children, tone = 'neutral', dot, className, title }: BadgeProps) {
  return (
    <span className={['badge', `badge-${tone}`, className ?? ''].filter(Boolean).join(' ')} title={title}>
      {dot ? <span className="badge-dot" aria-hidden="true" /> : null}
      {children}
    </span>
  );
}

/** Count bubble for nav items; `aria-live` so screen readers hear changes. */
export function CountBadge({ count, label }: { count: number; label: string }) {
  return (
    <span className="count-badge" aria-live="polite" aria-atomic="true">
      {count > 0 ? (
        <>
          <span aria-hidden="true">{count > 99 ? '99+' : count}</span>
          <span className="visually-hidden">
            {count} {label}
          </span>
        </>
      ) : null}
    </span>
  );
}

/** Tier colour + icon + word; colour is never the only signal. */
export function TierBadge({ tier, short = false }: { tier: InboxTier; short?: boolean }) {
  const tone: BadgeTone = tier === 'ready' ? 'brand' : tier === 'check' ? 'gold' : 'danger';
  const Icon = tier === 'ready' ? CheckCircleIcon : tier === 'check' ? QuestionIcon : AlertIcon;
  return (
    <span className={`tier-badge tier-${tier} badge badge-${tone}`}>
      <Icon size={14} />
      {short ? <span className="visually-hidden">{TIER_LABEL[tier]}</span> : TIER_LABEL[tier]}
    </span>
  );
}
